import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { BrutalCard } from "@/components/ui/BrutalCard";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { RefreshCw, Trash2 } from "lucide-react";

interface Coupon {
  id: string;
  code: string;
  description: string;
  discount_type: string;
  discount_value: number;
  current_uses: number;
  max_uses: number | null;
  valid_from: string;
  valid_until: string;
  is_active: boolean;
  applicable_plans: string[];
  stripe_coupon_id: string | null;
  duration_type: string;
  duration_in_months: number | null;
  created_at?: string;
}

interface CouponDetailModalProps {
  coupon: Coupon;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpdate?: () => void;
}

export default function CouponDetailModal({ 
  coupon, 
  open, 
  onOpenChange,
  onUpdate 
}: CouponDetailModalProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [syncing, setSyncing] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [currentCoupon, setCurrentCoupon] = useState<Coupon>(coupon);

  const handleSyncStripe = async () => {
    setSyncing(true);
    try {
      const { data, error } = await supabase.functions.invoke("sync-coupon-stripe", {
        body: { coupon_id: currentCoupon.id },
      });

      if (error) throw error;

      if (data.success) {
        if (data.synced) {
          toast({
            title: t("admin.coupons.toast.syncCompleted"),
            description: data.message,
          });
          if (data.updated_fields && data.updated_fields.length > 0) {
            toast({
              title: t("admin.coupons.toast.fieldsUpdated"),
              description: data.updated_fields.join(", "),
            });
          }
          // Aggiorna i dati della modale
          if (data.coupon) {
            setCurrentCoupon(data.coupon);
            onUpdate?.();
          }
        } else {
          toast({
            title: t("admin.coupons.toast.warning"),
            description: data.message,
            variant: "warning",
          });
        }
      } else {
        throw new Error(data.error || "Sync failed");
      }
    } catch (error: any) {
      console.error("Error syncing with Stripe:", error);
      toast({
        title: t("admin.coupons.toast.error"),
        description: error.message || t("admin.coupons.toast.errorSync"),
        variant: "error",
      });
    } finally {
      setSyncing(false);
    }
  };

  const handleToggleActive = async () => {
    setUpdating(true);
    try {
      const newActiveState = !currentCoupon.is_active;
      
      const { data, error } = await supabase.functions.invoke("update-coupon-admin", {
        body: { 
          id: currentCoupon.id,
          is_active: newActiveState
        },
      });

      if (error) throw error;

      if (data.success) {
        toast({
          title: t("admin.coupons.toast.success"),
          description: newActiveState ? t("admin.coupons.toast.couponActivated") : t("admin.coupons.toast.couponDeactivated"),
        });
        setCurrentCoupon({ ...currentCoupon, is_active: newActiveState });
        onUpdate?.();
        
        if (data.stripe_error) {
          toast({
            title: t("admin.coupons.toast.warning"),
            description: `Stripe: ${data.stripe_error}`,
            variant: "warning",
          });
        }
      } else {
        throw new Error(data.error || "Update failed");
      }
    } catch (error: any) {
      console.error("Error updating coupon:", error);
      toast({
        title: t("admin.coupons.toast.error"),
        description: error.message || t("admin.coupons.toast.errorUpdate"),
        variant: "error",
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm(t("admin.coupons.detailModal.confirmDelete", { code: currentCoupon.code }))) {
      return;
    }

    setDeleting(true);
    try {
      const { data, error } = await supabase.functions.invoke("delete-coupon-admin", {
        body: { id: currentCoupon.id },
      });

      if (error) throw error;

      if (data.success) {
        toast({
          title: t("admin.coupons.toast.success"),
          description: t("admin.coupons.toast.couponDeleted"),
        });
        
        if (data.stripe_error) {
          toast({
            title: t("admin.coupons.toast.warning"),
            description: t("admin.coupons.toast.dbUpdatedStripeError", { error: data.stripe_error }),
            variant: "warning",
          });
        }
        
        onUpdate?.();
        onOpenChange(false);
      } else {
        throw new Error(data.error || "Delete failed");
      }
    } catch (error: any) {
      console.error("Error deleting coupon:", error);
      toast({
        title: t("admin.coupons.toast.error"),
        description: error.message || t("admin.coupons.toast.errorDelete"),
        variant: "error",
      });
      setDeleting(false);
    }
  };

  const getDiscountDisplay = () => {
    if (currentCoupon.discount_type === "percentage") {
      return `${currentCoupon.discount_value}%`;
    } else if (currentCoupon.discount_type === "fixed") {
      return `€${currentCoupon.discount_value}`;
    } else {
      return t("admin.coupons.discountTypes.freeLabel");
    }
  };

  const getDurationDisplay = () => {
    if (currentCoupon.duration_type === 'repeating' && currentCoupon.duration_in_months === 3) {
      return t("admin.coupons.durations.oncePayment");
    } else if (currentCoupon.duration_type === 'forever') {
      return t("admin.coupons.durations.forever");
    } else if (currentCoupon.duration_type === 'repeating') {
      return t("admin.coupons.durations.months", { months: currentCoupon.duration_in_months });
    } else {
      return t("admin.coupons.durations.singlePayment");
    }
  };

  // Helper function to map plan IDs to translation keys
  const getPlanTranslationKey = (plan: string): string => {
    const keyMap: Record<string, string> = {
      'social_boost': 'socialBoost',
      'job_posting_single': 'jobSingle',
      'job_posting_pack_3': 'jobPack3',
      'job_posting_pack_5': 'jobPack5',
    };
    return keyMap[plan] || plan;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto border-[3px] border-dark shadow-[8px_8px_0px_0px] shadow-dark">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl flex items-center gap-3">
            {t("admin.coupons.detailModal.title")}
            <code className="font-mono text-primary">{currentCoupon.code}</code>
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-6 my-4">
          {/* Colonna Sinistra: Informazioni Base */}
          <BrutalCard className="p-6">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
              📋 {t("admin.coupons.detailModal.sectionBasic")}
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm text-muted-foreground">{t("admin.coupons.detailModal.couponCode")}</label>
                <p className="font-mono font-bold text-lg text-primary">{currentCoupon.code}</p>
              </div>

              <Separator />

              <div>
                <label className="text-sm text-muted-foreground">{t("admin.coupons.detailModal.description")}</label>
                <p className="font-body">{currentCoupon.description || "—"}</p>
              </div>

              <Separator />

              <div>
                <label className="text-sm text-muted-foreground">{t("admin.coupons.detailModal.discountType")}</label>
                <p className="font-bold">
                  {currentCoupon.discount_type === "percentage" ? t("admin.coupons.discountTypes.percentage") : 
                   currentCoupon.discount_type === "fixed" ? t("admin.coupons.discountTypes.fixed") : t("admin.coupons.discountTypes.full")}
                </p>
              </div>

              <div>
                <label className="text-sm text-muted-foreground">{t("admin.coupons.detailModal.discountValue")}</label>
                <p className="font-bold text-xl text-primary">{getDiscountDisplay()}</p>
              </div>

              <Separator />

              <div>
                <label className="text-sm text-muted-foreground">{t("admin.coupons.detailModal.discountDuration")}</label>
                <p className="font-bold">{getDurationDisplay()}</p>
              </div>

              {currentCoupon.applicable_plans && currentCoupon.applicable_plans.length > 0 && (
                <>
                  <Separator />
                  <div>
                    <label className="text-sm text-muted-foreground">{t("admin.coupons.detailModal.applicablePlans")}</label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {currentCoupon.applicable_plans.map((plan) => (
                        <Badge key={plan} variant="outline">
                          {t(`admin.coupons.plans.${getPlanTranslationKey(plan)}`)}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </BrutalCard>

          {/* Colonna Destra: Utilizzi e Validità */}
          <BrutalCard className="p-6">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
              📊 {t("admin.coupons.detailModal.sectionUsage")}
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm text-muted-foreground">{t("admin.coupons.detailModal.uses")}</label>
                <p className="font-bold text-xl">
                  {currentCoupon.current_uses} / {currentCoupon.max_uses || "∞"}
                </p>
              </div>

              <Separator />

              <div>
                <label className="text-sm text-muted-foreground">{t("admin.coupons.detailModal.validFrom")}</label>
                <p className="font-bold">
                  {format(new Date(currentCoupon.valid_from), "dd/MM/yyyy HH:mm")}
                </p>
              </div>

              <div>
                <label className="text-sm text-muted-foreground">{t("admin.coupons.detailModal.validUntil")}</label>
                <p className="font-bold">
                  {format(new Date(currentCoupon.valid_until), "dd/MM/yyyy HH:mm")}
                </p>
              </div>

              <Separator />

              <div>
                <label className="text-sm text-muted-foreground mb-2 block">{t("admin.coupons.detailModal.status")}</label>
                <div className="flex items-center gap-3">
                  <Switch 
                    checked={currentCoupon.is_active} 
                    onCheckedChange={handleToggleActive}
                    disabled={updating || syncing || deleting}
                  />
                  <span className="font-bold">
                    {currentCoupon.is_active ? t("admin.coupons.detailModal.statusActive") : t("admin.coupons.detailModal.statusInactive")}
                  </span>
                </div>
              </div>

              <Separator />

              <div>
                <label className="text-sm text-muted-foreground">{t("admin.coupons.detailModal.stripeId")}</label>
                {currentCoupon.stripe_coupon_id ? (
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">
                      {t("admin.coupons.table.badgeSync")}
                    </Badge>
                    <code className="text-xs">{currentCoupon.stripe_coupon_id}</code>
                  </div>
                ) : (
                  <Badge variant="outline" className="bg-gray-50 text-gray-500 border-gray-300">
                    {t("admin.coupons.badges.onlyDatabase")}
                  </Badge>
                )}
              </div>

              {currentCoupon.created_at && (
                <>
                  <Separator />
                  <div>
                    <label className="text-sm text-muted-foreground">{t("admin.coupons.detailModal.createdAt")}</label>
                    <p className="font-body text-sm">
                      {format(new Date(currentCoupon.created_at), "dd/MM/yyyy HH:mm")}
                    </p>
                  </div>
                </>
              )}
            </div>
          </BrutalCard>
        </div>

        <DialogFooter className="flex justify-between gap-3">
          {/* Pulsante Elimina a sinistra */}
          <BrutalButton
            onClick={handleDelete}
            variant="danger"
            disabled={syncing || updating || deleting}
            className="flex items-center gap-2"
          >
            <Trash2 className="w-4 h-4" />
            {deleting ? t("admin.coupons.detailModal.buttonDeleting") : t("admin.coupons.detailModal.buttonDelete")}
          </BrutalButton>

          {/* Pulsanti Sync e Chiudi a destra */}
          <div className="flex gap-3">
            <BrutalButton
              onClick={handleSyncStripe}
              variant="primary"
              disabled={syncing || updating || deleting}
              className="flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} />
              {syncing ? t("admin.coupons.detailModal.buttonSyncing") : t("admin.coupons.detailModal.buttonSync")}
            </BrutalButton>
            
            <BrutalButton
              onClick={() => onOpenChange(false)}
              variant="outline"
              disabled={syncing || updating || deleting}
            >
              {t("admin.coupons.detailModal.buttonClose")}
            </BrutalButton>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
